import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamsAPI, matchesAPI } from '../services/api';
import { FaHome, FaUsers, FaFutbol, FaNewspaper, FaCalendar, FaFileAlt, FaCog, FaChartBar, FaBullhorn, FaGavel, FaUserTie, FaTrophy, FaClipboardCheck, FaExclamationTriangle, FaBars, FaTimes, FaImages, FaTv, FaYoutube, FaMinusCircle } from 'react-icons/fa';
import NewsTickerSection from '../components/admin/NewsTickerSection';
import PlayersSection from '../components/admin/PlayersSection';
import SettingsSection from '../components/admin/SettingsSection';
import DesignationsSection from '../components/admin/DesignationsSection';
import ClubNewsSection from '../components/admin/ClubNewsSection';
import CommitteesSection from '../components/admin/CommitteesSection';
import ResultsManagementSection from '../components/admin/ResultsManagementSection';
import DisciplineSection from '../components/admin/DisciplineSection';
import CommuniquesSection from '../components/admin/CommuniquesSection';
import MediaSection from '../components/admin/MediaSection';
import NewsSection from '../components/admin/NewsSection';
import PointsDeductionSection from '../components/admin/PointsDeductionSection';
import LiveManagementSection from '../components/admin/LiveManagementSection';
import { useLanguage } from '../context/LanguageContext';


const AdminDashboardV2 = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');
  const [formData, setFormData] = useState({});
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [matchFilterRound, setMatchFilterRound] = useState('');
  const [matchFilterClub, setMatchFilterClub] = useState('');
  const [matchFilterDate, setMatchFilterDate] = useState('');
  const [matchFilterGroup, setMatchFilterGroup] = useState('');
  const [matchFilterStatus, setMatchFilterStatus] = useState('');
  const [matchFilterLeg, setMatchFilterLeg] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [teamGroupFilter, setTeamGroupFilter] = useState('');
  const [matchPage, setMatchPage] = useState(1);
  const matchPageSize = 12;
  const useHorizontalNav = true;
  const topBarHeight = 56;
  const safeTeams = Array.isArray(teams) ? teams : [];
  const safeMatches = Array.isArray(matches) ? matches : [];

  // Accès réservé aux admins connectés : redirection si pas de token
  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/admin', { replace: true });
      return;
    }
  }, [navigate]);

  // Forcer le layout LTR et éviter tout clipping (sidebar à gauche, contenu visible)
  useEffect(() => {
    const id = 'admin-dashboard-layout-fix';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      .admin-dashboard-wrap { direction: ltr !important; }
      .admin-dashboard-wrap .admin-dashboard-sidebar { left: 0 !important; right: auto !important; }
      .admin-dashboard-wrap .admin-dashboard-topbar { left: 0 !important; right: 0 !important; }
      .admin-dashboard-main { overflow-x: auto !important; overflow-y: auto !important; box-sizing: border-box !important; }
    `;
    document.head.appendChild(style);
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);

  useEffect(() => {
    if (localStorage.getItem('token')) loadData();
  }, []);

  const loadData = async () => {
    try {
      const [teamsRes, matchesRes] = await Promise.all([
        teamsAPI.getAll(),
        matchesAPI.getAll()
      ]);
      setTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
      setMatches(Array.isArray(matchesRes.data) ? matchesRes.data : []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('city', formData.city || '');
      data.append('stadium', formData.stadium || '');
      data.append('group_number', formData.group_number ?? 1);
      data.append('history', formData.history || '');
      data.append('history_ar', formData.history_ar || '');
      data.append('board_members', formData.board_members || '');
      data.append('board_members_ar', formData.board_members_ar || '');
      if (formData.logo) {
        data.append('logo', formData.logo);
      } else if (formData.id && formData.logo_url) {
        data.append('logo_url', formData.logo_url);
      }

      if (formData.id) {
        await teamsAPI.update(formData.id, data);
      } else {
        await teamsAPI.create(data);
      }
      loadData();
      setFormData({});
      setTeamModalOpen(false);
      alert('Équipe enregistrée!');
    } catch (error) {
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleMatchSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await matchesAPI.update(formData.id, formData);
      } else {
        await matchesAPI.create(formData);
      }
      loadData();
      setFormData({});
      alert(language === 'ar' ? 'تم حفظ المباراة!' : 'Match enregistré!');
    } catch (error) {
      console.error('Match Submit Error:', error);
      const msg = error.response?.data?.error || error.message;
      alert((language === 'ar' ? 'خطأ أثناء الحفظ: ' : 'Erreur lors de l\'enregistrement: ') + msg);
    }
  };

  const deleteTeam = async (id) => {
    if (window.confirm('Supprimer cette équipe?')) {
      try {
        await teamsAPI.delete(id);
        loadData();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const deleteMatch = async (id) => {
    if (window.confirm('Supprimer ce match?')) {
      try {
        await matchesAPI.delete(id);
        loadData();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const menuItems = [
    { id: 'overview', icon: FaHome, label: t('overview') },
    { id: 'teams', icon: FaUsers, label: t('teams') },
    { id: 'matches', icon: FaFutbol, label: t('matches') },
    { id: 'results', icon: FaClipboardCheck, label: t('results') },
    { id: 'live_management', icon: FaTv, label: language === 'ar' ? 'البث المباشر' : 'Espace Live' },
    { id: 'ticker', icon: FaBullhorn, label: t('ticker') },

    { id: 'media', icon: FaImages, label: t('media') },
    { id: 'club-news', icon: FaNewspaper, label: t('clubNews') },
    { id: 'news', icon: FaNewspaper, label: language === 'ar' ? 'أخبار الرابطة' : 'Actualités Ligue' },
    { id: 'committees', icon: FaUserTie, label: t('committees') },
    { id: 'designations', icon: FaGavel, label: t('designations') },
    { id: 'discipline', icon: FaExclamationTriangle, label: t('discipline') },
    { id: 'communiques', icon: FaFileAlt, label: t('communiques') },
    { id: 'players', icon: FaTrophy, label: t('players') },
    { id: 'penalties', icon: FaMinusCircle, label: language === 'ar' ? 'سحب النقاط' : 'Retrait de Points' },
    { id: 'settings', icon: FaCog, label: t('settings') }
  ];

  const translatedMenuItems = menuItems;
  const sidebarWidth = useHorizontalNav ? 0 : (sidebarCollapsed ? 72 : 320);

  return (
    <div className="admin-dashboard-wrap" dir="ltr" style={styles.dashboardWrap} aria-label="Admin Dashboard">
      <div style={styles.container}>
        {/* Emplacement jaune : menu admin (Vue d'ensemble, Équipes, Matchs, etc.) */}
        {useHorizontalNav ? (
          <div style={styles.adminMenuYellowStrip}>
            <header className="admin-dashboard-topbar" style={styles.topBar} aria-label="Menu">
              <div style={styles.topBarBrand}>
                <button
                  type="button"
                  onClick={() => setActiveSection('overview')}
                  style={styles.topBarLogoCarreau}
                  title={t('overview')}
                  aria-label={t('overview')}
                >
                  <img src="/images/logo.png" alt="" style={styles.topBarLogoImg} onError={(e) => { e.target.style.display = 'none'; }} />
                </button>
                <span style={styles.topBarTitleSmall}>{t('dashboard')}</span>
                <span style={styles.topBarVersion}>v2.0</span>
              </div>
              <nav style={styles.topBarNav} role="navigation">
                {translatedMenuItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    style={{
                      ...styles.menuItemHorizontal,
                      ...(activeSection === item.id ? styles.menuItemHorizontalActive : {})
                    }}
                  >
                    <item.icon style={styles.menuIconHorizontal} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>
            </header>
          </div>
        ) : (
          <aside className="admin-dashboard-sidebar" style={{ ...styles.sidebar, width: sidebarWidth, left: 0, right: 'auto', top: 0 }} aria-label="Menu">
            <div style={styles.sidebarHeader}>
              {!sidebarCollapsed && (
                <>
                  <h2 style={styles.sidebarTitle}>{t('dashboard')}</h2>
                  <p style={styles.version}>v2.0 - {language === 'ar' ? 'إدارة كاملة' : 'Gestion Complète'}</p>
                </>
              )}
              <button type="button" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={styles.collapseBtn} title={sidebarCollapsed ? (language === 'ar' ? 'توسيع' : 'Agrandir') : (language === 'ar' ? 'تصغير' : 'Réduire')}>
                {sidebarCollapsed ? <FaBars size={20} /> : <FaTimes size={18} />}
              </button>
            </div>
            <nav style={styles.sidebarNav}>
              {translatedMenuItems.map(item => (
                <button key={item.id} onClick={() => setActiveSection(item.id)} style={{ ...styles.menuItem, ...(activeSection === item.id ? styles.menuItemActive : {}), justifyContent: sidebarCollapsed ? 'center' : 'flex-start', padding: sidebarCollapsed ? '15px' : '15px 20px' }}>
                  <item.icon style={styles.menuIcon} />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </button>
              ))}
            </nav>
          </aside>
        )}

        <main
          className="admin-dashboard-main"
          dir={language === 'ar' ? 'rtl' : 'ltr'}
          style={{
            ...styles.mainContent,
            marginLeft: sidebarWidth,
            marginTop: useHorizontalNav ? topBarHeight : 0,
            width: useHorizontalNav ? '100%' : `calc(100% - ${sidebarWidth}px)`,
            minWidth: 0,
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
          role="main"
        >
          {/* Overview - Tableau de bord avec statistiques et groupes */}
          {activeSection === 'overview' && (
            <div>
              <h1 style={styles.pageTitle}>{language === 'ar' ? 'لوحة التحكم' : 'Tableau de bord'}</h1>
              <p style={styles.pageSubtitle}>
                {language === 'ar' ? 'نظرة عامة على الإحصائيات والمجموعات' : 'Vue d\'ensemble des statistiques et des groupes'}
              </p>

              {/* Cartes statistiques principales */}
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <FaUsers style={styles.statIcon} />
                  <h3 style={styles.statNumber}>{teams.length}</h3>
                  <p style={styles.statLabel}>{language === 'ar' ? 'الفرق' : 'Équipes'}</p>
                </div>
                <div style={styles.statCard}>
                  <FaFutbol style={styles.statIcon} />
                  <h3 style={styles.statNumber}>{matches.length}</h3>
                  <p style={styles.statLabel}>{language === 'ar' ? 'المباريات' : 'Matchs'}</p>
                </div>
                <div style={styles.statCard}>
                  <FaChartBar style={styles.statIcon} />
                  <h3 style={styles.statNumber}>{matches.filter(m => m.status === 'finished').length}</h3>
                  <p style={styles.statLabel}>{language === 'ar' ? 'المباريات المنتهية' : 'Matchs terminés'}</p>
                </div>
                <div style={styles.statCard}>
                  <FaCalendar style={styles.statIcon} />
                  <h3 style={styles.statNumber}>{matches.filter(m => m.status === 'upcoming').length}</h3>
                  <p style={styles.statLabel}>{language === 'ar' ? 'المباريات القادمة' : 'Matchs à venir'}</p>
                </div>
              </div>

              {/* Groupes de données - Répartition par groupe */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>
                  {language === 'ar' ? 'توزيع الفرق حسب المجموعة' : 'Répartition des équipes par groupe'}
                </h2>
                <div style={styles.groupsStatsGrid}>
                  {[1, 2, 3, 4, 5, 6].map(g => {
                    const count = teams.filter(t => (t.group_number || 1) === g).length;
                    return (
                      <div key={g} style={styles.groupStatCard}>
                        <span style={styles.groupStatLabel}>
                          {language === 'ar' ? 'المجموعة' : 'Groupe'} {g}
                        </span>
                        <span style={styles.groupStatNumber}>{count}</span>
                        <span style={styles.groupStatTeams}>
                          {language === 'ar' ? 'فريق' : 'équipes'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Statistiques matchs (statut) */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>
                  {language === 'ar' ? 'إحصائيات المباريات' : 'Statistiques des matchs'}
                </h2>
                <div style={styles.statsGrid}>
                  <div style={{ ...styles.statCard, background: 'linear-gradient(135deg, #28a74522, #28a74511)' }}>
                    <FaClipboardCheck style={{ ...styles.statIcon, color: '#28a745' }} />
                    <h3 style={styles.statNumber}>{matches.filter(m => m.status === 'finished').length}</h3>
                    <p style={styles.statLabel}>{language === 'ar' ? 'منتهية' : 'Terminés'}</p>
                  </div>
                  <div style={{ ...styles.statCard, background: 'linear-gradient(135deg, #ffc10722, #ffc10711)' }}>
                    <FaCalendar style={{ ...styles.statIcon, color: '#e0a800' }} />
                    <h3 style={styles.statNumber}>{matches.filter(m => m.status === 'upcoming').length}</h3>
                    <p style={styles.statLabel}>{language === 'ar' ? 'قادمة' : 'À venir'}</p>
                  </div>
                  <div style={{ ...styles.statCard, background: 'linear-gradient(135deg, #dc354522, #dc354511)' }}>
                    <FaFutbol style={{ ...styles.statIcon, color: '#dc3545' }} />
                    <h3 style={styles.statNumber}>{matches.filter(m => m.status === 'live').length}</h3>
                    <p style={styles.statLabel}>{language === 'ar' ? 'مباشر' : 'En direct'}</p>
                  </div>
                </div>
              </div>

              {/* Liens rapides */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>
                  {language === 'ar' ? 'وصول سريع' : 'Accès rapide'}
                </h2>
                <div style={styles.quickLinksGrid}>
                  {[
                    { id: 'results', icon: FaClipboardCheck, label: language === 'ar' ? 'النتائج' : 'Résultats' },
                    { id: 'matches', icon: FaFutbol, label: language === 'ar' ? 'المباريات' : 'Matchs' },
                    { id: 'teams', icon: FaUsers, label: language === 'ar' ? 'الفرق' : 'Équipes' },
                    { id: 'designations', icon: FaGavel, label: language === 'ar' ? 'التعيينات' : 'Désignations' },
                    { id: 'discipline', icon: FaExclamationTriangle, label: language === 'ar' ? 'الانضباط' : 'Discipline' },
                    { id: 'settings', icon: FaCog, label: language === 'ar' ? 'الإعدادات' : 'Paramètres' }
                  ].map(item => (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      style={styles.quickLinkBtn}
                    >
                      <item.icon style={styles.quickLinkIcon} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Teams */}
          {activeSection === 'teams' && (
            <div>
              <h1 style={styles.pageTitle}>{language === 'ar' ? 'إدارة الفرق' : 'Gestion des Équipes'}</h1>
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>{language === 'ar' ? 'قائمة الفرق' : 'Liste des équipes'} ({teams.length})</h2>

                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder={language === 'ar' ? 'بحث باسم الفريق...' : 'Rechercher une équipe par nom...'}
                    value={teamFilter}
                    onChange={(e) => setTeamFilter(e.target.value)}
                    style={{ ...styles.filterInput, flex: 2, minWidth: '200px' }}
                  />
                  <select
                    value={teamGroupFilter}
                    onChange={(e) => setTeamGroupFilter(e.target.value)}
                    style={{ ...styles.filterSelect, flex: 1, minWidth: '150px' }}
                  >
                    <option value="">{language === 'ar' ? 'كل المجموعات' : 'Tous les groupes'}</option>
                    {[1, 2, 3, 4, 5, 6].map(g => (
                      <option key={g} value={g}>{language === 'ar' ? `المجموعة ${g}` : `Groupe ${g}`}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => { setFormData({}); setTeamModalOpen(true); }}
                    style={{ ...styles.addTeamBtn, marginBottom: 0 }}
                  >
                    + {language === 'ar' ? 'إضافة فريق' : 'Ajouter une équipe'}
                  </button>
                </div>

                <div style={styles.teamsGrid}>
                  {teams.filter(t => {
                    const nameMatch = (t.name || '').toLowerCase().includes((teamFilter || '').toLowerCase());
                    const groupMatch = teamGroupFilter === '' || String(t.group_number) === String(teamGroupFilter);
                    return nameMatch && groupMatch;
                  }).map(team => (
                    <div key={team.id} style={styles.teamCard}>
                      {team.logo_url && (
                        <img src={team.logo_url} alt={team.name} style={styles.teamLogo} />
                      )}
                      <h3 style={styles.teamName}>{team.name}</h3>
                      <p style={styles.teamInfo}>{team.city}</p>
                      <p style={styles.teamInfo}>Groupe {team.group_number}</p>
                      <div style={styles.cardActions}>
                        <button onClick={() => { setFormData({ ...team }); setTeamModalOpen(true); }} style={styles.editBtn}>
                          {t('edit')}
                        </button>
                        <button onClick={() => deleteTeam(team.id)} style={styles.deleteBtn}>
                          {t('delete')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Popup modification / ajout équipe */}
              {teamModalOpen && (
                <div style={styles.modalOverlay} onClick={() => { setTeamModalOpen(false); setFormData({}); }}>
                  <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
                    <div style={styles.modalHeader}>
                      <h2 style={styles.modalTitle}>
                        {formData.id ? (language === 'ar' ? 'تعديل فريق' : 'Modifier une équipe') : (language === 'ar' ? 'إضافة فريق' : 'Ajouter une équipe')}
                      </h2>
                      <button type="button" style={styles.modalClose} onClick={() => { setTeamModalOpen(false); setFormData({}); }} aria-label="Fermer">
                        <FaTimes size={22} />
                      </button>
                    </div>
                    <div style={{ padding: '24px' }}>
                      <form onSubmit={handleTeamSubmit} style={styles.form}>
                        <input
                          type="text"
                          placeholder={language === 'ar' ? 'اسم الفريق' : 'Nom de l\'équipe'}
                          value={formData.name || ''}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          style={styles.input}
                          required
                        />
                        <input type="text" placeholder={language === 'ar' ? 'المدينة' : 'Ville'} value={formData.city || ''} onChange={(e) => setFormData({ ...formData, city: e.target.value })} style={styles.input} />
                        <input type="text" placeholder={language === 'ar' ? 'الملعب' : 'Stade'} value={formData.stadium || ''} onChange={(e) => setFormData({ ...formData, stadium: e.target.value })} style={styles.input} />
                        <select value={formData.group_number || 1} onChange={(e) => setFormData({ ...formData, group_number: e.target.value })} style={styles.input}>
                          {[1, 2, 3, 4, 5, 6].map(g => (
                            <option key={g} value={g}>{language === 'ar' ? 'المجموعة' : 'Groupe'} {g}</option>
                          ))}
                        </select>
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '15px' }}>
                          <textarea placeholder="Histoire du club (FR)" value={formData.history || ''} onChange={(e) => setFormData({ ...formData, history: e.target.value })} style={{ ...styles.input, height: '100px', minWidth: 0 }} />
                          <textarea placeholder="تاريخ النادي (AR)" value={formData.history_ar || ''} onChange={(e) => setFormData({ ...formData, history_ar: e.target.value })} style={{ ...styles.input, height: '100px', textAlign: 'right', minWidth: 0 }} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '15px' }}>
                          <textarea placeholder="الهيئة المديرة (FR)" value={formData.board_members || ''} onChange={(e) => setFormData({ ...formData, board_members: e.target.value })} style={{ ...styles.input, height: '100px', minWidth: 0 }} />
                          <textarea placeholder="الهيئة المديرة و المسؤولين (AR)" value={formData.board_members_ar || ''} onChange={(e) => setFormData({ ...formData, board_members_ar: e.target.value })} style={{ ...styles.input, height: '100px', textAlign: 'right', minWidth: 0 }} />
                        </div>
                        <input type="file" accept="image/*" onChange={(e) => setFormData({ ...formData, logo: e.target.files[0] })} style={styles.input} />
                        <div style={styles.formActions}>
                          <button type="submit" style={styles.submitBtn}>{formData.id ? t('edit') : t('add')}</button>
                          <button type="button" onClick={() => { setTeamModalOpen(false); setFormData({}); }} style={styles.cancelBtn}>{t('cancel')}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'matches' && (
            <div>
              <h1 style={styles.pageTitle}>{language === 'ar' ? 'إدارة المباريات' : 'Gestion des Matchs'}</h1>
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>
                  {formData.id ? (language === 'ar' ? 'تعديل مباراة' : 'Modifier un match') : (language === 'ar' ? 'إنشاء مباراة' : 'Créer un match')}
                </h2>
                <div style={styles.infoBoxSync}>
                  <p>💡 {language === 'ar' ? 'تعديل النتائج هنا يحدث نفس قاعدة بيانات قسم "النتائج".' : 'Modifier les résultats ici met à jour la même base de données que la section "Résultats".'}</p>
                </div>
                <form onSubmit={handleMatchSubmit} style={styles.form}>
                  <select
                    value={formData.home_team_id || ''}
                    onChange={(e) => setFormData({ ...formData, home_team_id: e.target.value })}
                    style={styles.input}
                    required
                  >
                    <option value="">{language === 'ar' ? 'الفريق المضيف' : 'Équipe domicile'}</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                  <select
                    value={formData.away_team_id || ''}
                    onChange={(e) => setFormData({ ...formData, away_team_id: e.target.value })}
                    style={styles.input}
                    required
                  >
                    <option value="">{language === 'ar' ? 'الفريق الضيف' : 'Équipe extérieur'}</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                  <input
                    type="datetime-local"
                    value={formData.match_date || ''}
                    onChange={(e) => setFormData({ ...formData, match_date: e.target.value })}
                    style={styles.input}
                    required
                  />
                  <input
                    type="text"
                    placeholder={language === 'ar' ? 'الجولة' : 'Round (ex: 1, 2, Finale)'}
                    value={formData.round || ''}
                    onChange={(e) => setFormData({ ...formData, round: e.target.value })}
                    style={styles.input}
                    required
                  />
                  <select
                    value={formData.leg || 'aller'}
                    onChange={(e) => setFormData({ ...formData, leg: e.target.value })}
                    style={styles.input}
                    required
                  >
                    <option value="aller">{language === 'ar' ? 'ذهاب' : 'Aller'}</option>
                    <option value="retour">{language === 'ar' ? 'إياب' : 'Retour'}</option>
                  </select>
                  <input
                    type="text"
                    placeholder={language === 'ar' ? 'الملعب' : 'Stade / Lieu'}
                    value={formData.venue || ''}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    style={styles.input}
                  />
                  {formData.id && (
                    <>
                      <div style={styles.scoreRow}>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>{language === 'ar' ? 'أهداف المضيف' : 'Score domicile'}</label>
                          <input
                            type="number"
                            value={formData.home_score || 0}
                            onChange={(e) => setFormData({ ...formData, home_score: e.target.value })}
                            style={styles.input}
                            min="0"
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>{language === 'ar' ? 'أهداف الضيف' : 'Score extérieur'}</label>
                          <input
                            type="number"
                            value={formData.away_score || 0}
                            onChange={(e) => setFormData({ ...formData, away_score: e.target.value })}
                            style={styles.input}
                            min="0"
                          />
                        </div>
                      </div>

                      <div style={styles.scoreRow}>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>🟨 {language === 'ar' ? 'صفراء (مضيف)' : 'Jaunes (Home)'}</label>
                          <input
                            type="number"
                            value={formData.yellow_cards_home || 0}
                            onChange={(e) => setFormData({ ...formData, yellow_cards_home: e.target.value })}
                            style={styles.input}
                            min="0"
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>🟨 {language === 'ar' ? 'صفراء (ضيف)' : 'Jaunes (Away)'}</label>
                          <input
                            type="number"
                            value={formData.yellow_cards_away || 0}
                            onChange={(e) => setFormData({ ...formData, yellow_cards_away: e.target.value })}
                            style={styles.input}
                            min="0"
                          />
                        </div>
                      </div>

                      <div style={styles.scoreRow}>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>🟥 {language === 'ar' ? 'حمراء (مضيف)' : 'Rouges (Home)'}</label>
                          <input
                            type="number"
                            value={formData.red_cards_home || 0}
                            onChange={(e) => setFormData({ ...formData, red_cards_home: e.target.value })}
                            style={styles.input}
                            min="0"
                          />
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>🟥 {language === 'ar' ? 'حمراء (ضيف)' : 'Rouges (Away)'}</label>
                          <input
                            type="number"
                            value={formData.red_cards_away || 0}
                            onChange={(e) => setFormData({ ...formData, red_cards_away: e.target.value })}
                            style={styles.input}
                            min="0"
                          />
                        </div>
                      </div>

                      <div style={styles.formGroup}>
                        <label style={styles.label}>{language === 'ar' ? 'الحالة' : 'Statut'}</label>
                        <select
                          value={formData.status || 'upcoming'}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          style={styles.input}
                        >
                          <option value="upcoming">{language === 'ar' ? 'قادمة' : 'À venir'}</option>
                          <option value="live">{language === 'ar' ? 'مباشر' : 'En direct'}</option>
                          <option value="finished">{language === 'ar' ? 'منتهية' : 'Terminé'}</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div style={styles.formActions}>
                    <button type="submit" style={styles.submitBtn}>
                      {formData.id ? (language === 'ar' ? 'تعديل' : 'Modifier') : (language === 'ar' ? 'إنشاء' : 'Créer')}
                    </button>
                    {formData.id && (
                      <button type="button" onClick={() => setFormData({})} style={styles.cancelBtn}>
                        {language === 'ar' ? 'إلغاء' : 'Annuler'}
                      </button>
                    )}
                  </div>
                </form>
              </div>

              <div style={styles.card}>
                <h2 style={styles.cardTitle}>
                  {language === 'ar' ? 'قائمة المباريات' : 'Liste des matchs'} ({matches.length})
                </h2>
                {/* Filtres simples */}
                <div style={styles.matchFilters}>
                  <input
                    type="text"
                    placeholder={language === 'ar' ? 'بحث بالفرق...' : 'Recherche par club...'}
                    value={matchFilterClub}
                    onChange={(e) => { setMatchFilterClub(e.target.value); setMatchPage(1); }}
                    style={styles.filterInput}
                  />
                  <select
                    value={matchFilterRound}
                    onChange={(e) => { setMatchFilterRound(e.target.value); setMatchPage(1); }}
                    style={styles.filterSelect}
                  >
                    <option value="">{language === 'ar' ? 'كل الجولات' : 'Toutes les journées'}</option>
                    {[...new Set(matches.map(m => m.round).filter(Boolean))].sort((a, b) => Number(a) - Number(b)).map(r => (
                      <option key={r} value={r}>{language === 'ar' ? `الجولة ${r}` : `Journée ${r}`}</option>
                    ))}
                  </select>
                  <select
                    value={matchFilterGroup}
                    onChange={(e) => { setMatchFilterGroup(e.target.value); setMatchPage(1); }}
                    style={styles.filterSelect}
                  >
                    <option value="">{language === 'ar' ? 'كل المجموعات' : 'Tous les groupes'}</option>
                    {[1, 2, 3, 4, 5, 6].map(g => (
                      <option key={g} value={g}>{language === 'ar' ? `المجموعة ${g}` : `Groupe ${g}`}</option>
                    ))}
                  </select>
                  <select
                    value={matchFilterStatus}
                    onChange={(e) => { setMatchFilterStatus(e.target.value); setMatchPage(1); }}
                    style={styles.filterSelect}
                  >
                    <option value="">{language === 'ar' ? 'كل الحالات' : 'Tous les statuts'}</option>
                    <option value="upcoming">{language === 'ar' ? 'قادمة' : 'À venir'}</option>
                    <option value="live">{language === 'ar' ? 'مباشر' : 'En direct'}</option>
                    <option value="finished">{language === 'ar' ? 'منتهية' : 'Terminé'}</option>
                  </select>
                  <select
                    value={matchFilterLeg}
                    onChange={(e) => { setMatchFilterLeg(e.target.value); setMatchPage(1); }}
                    style={styles.filterSelect}
                  >
                    <option value="">{language === 'ar' ? 'كل المراحل' : 'Toutes les phases'}</option>
                    <option value="aller">{language === 'ar' ? 'ذهاب' : 'Aller'}</option>
                    <option value="retour">{language === 'ar' ? 'إياب' : 'Retour'}</option>
                  </select>
                  <input
                    type="date"
                    value={matchFilterDate}
                    onChange={(e) => { setMatchFilterDate(e.target.value); setMatchPage(1); }}
                    style={styles.filterInput}
                    title={language === 'ar' ? 'تاريخ المباراة' : 'Date du match'}
                  />
                  {(matchFilterClub || matchFilterRound || matchFilterDate || matchFilterGroup || matchFilterStatus || matchFilterLeg) && (
                    <button
                      type="button"
                      onClick={() => {
                        setMatchFilterClub('');
                        setMatchFilterRound('');
                        setMatchFilterDate('');
                        setMatchFilterGroup('');
                        setMatchFilterStatus('');
                        setMatchFilterLeg('');
                        setMatchPage(1);
                      }}
                      style={styles.filterReset}
                    >
                      {language === 'ar' ? 'إعادة تعيين' : 'Réinitialiser'}
                    </button>
                  )}
                </div>
                {/* Liste filtrée + pagination */}
                {(() => {
                  const q = (matchFilterClub || '').toLowerCase();
                  const filtered = matches.filter(m => {
                    if (matchFilterRound && String(m.round) !== String(matchFilterRound)) return false;
                    if (matchFilterDate && new Date(m.match_date).toISOString().slice(0, 10) !== matchFilterDate) return false;
                    if (matchFilterGroup && String(m.home_team_group) !== String(matchFilterGroup) && String(m.away_team_group) !== String(matchFilterGroup)) return false;
                    if (matchFilterStatus && m.status !== matchFilterStatus) return false;
                    if (matchFilterLeg && m.leg !== matchFilterLeg) return false;
                    if (q && !(m.home_team_name || '').toLowerCase().includes(q) && !(m.away_team_name || '').toLowerCase().includes(q)) return false;
                    return true;
                  });
                  const totalPages = Math.max(1, Math.ceil(filtered.length / matchPageSize));
                  const page = Math.min(matchPage, totalPages);
                  const start = (page - 1) * matchPageSize;
                  const pageMatches = filtered.slice(start, start + matchPageSize);
                  return (
                    <>
                      <p style={styles.matchCount}>
                        {language === 'ar' ? `${filtered.length} مباراة` : `${filtered.length} match(s)`}
                        {totalPages > 1 && ` · ${language === 'ar' ? 'صفحة' : 'page'} ${page} / ${totalPages}`}
                      </p>
                      {pageMatches.length === 0 ? (
                        <p style={{ color: '#666', textAlign: 'center', padding: '24px' }}>
                          {language === 'ar' ? 'لا توجد مباريات تطابق الفلتر.' : 'Aucun match ne correspond aux filtres.'}
                        </p>
                      ) : (
                        <div style={styles.matchCardsGrid}>
                          {pageMatches.map(match => (
                            <div key={match.id} style={styles.matchCard}>
                              <div style={styles.matchCardRound}>
                                {language === 'ar' ? `الجولة ${match.round || '-'}` : `J${match.round || '-'}`}
                                <span style={{ marginLeft: '10px', fontSize: '12px', background: '#eee', padding: '2px 6px', borderRadius: '10px' }}>
                                  {match.leg === 'retour'
                                    ? (language === 'ar' ? 'إياب' : 'Retour')
                                    : (language === 'ar' ? 'ذهاب' : 'Aller')}
                                </span>
                              </div>
                              <div style={styles.matchCardDate}>
                                {new Date(match.match_date).toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                              <div style={styles.matchCardTeams}>
                                <span style={styles.matchCardTeam}>{match.home_team_name}</span>
                                <span style={styles.matchCardScore}>{match.home_score ?? 0} - {match.away_score ?? 0}</span>
                                <span style={styles.matchCardTeam}>{match.away_team_name}</span>
                              </div>
                              <div style={styles.matchCardActions}>
                                <button onClick={() => setFormData({ ...match, match_date: match.match_date?.slice(0, 16) })} style={styles.editBtn}>
                                  {language === 'ar' ? 'تعديل' : 'Modifier'}
                                </button>
                                <button onClick={() => deleteMatch(match.id)} style={styles.deleteBtn}>
                                  {language === 'ar' ? 'حذف' : 'Supprimer'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {totalPages > 1 && (
                        <div style={styles.pagination}>
                          <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setMatchPage(p => Math.max(1, p - 1))}
                            style={{ ...styles.pageBtn, opacity: page <= 1 ? 0.5 : 1 }}
                          >
                            {language === 'ar' ? 'السابق' : 'Précédent'}
                          </button>
                          <span style={styles.pageInfo}>{page} / {totalPages}</span>
                          <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setMatchPage(p => Math.min(totalPages, p + 1))}
                            style={{ ...styles.pageBtn, opacity: page >= totalPages ? 0.5 : 1 }}
                          >
                            {language === 'ar' ? 'التالي' : 'Suivant'}
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Results Management */}
          {activeSection === 'results' && <ResultsManagementSection />}

          {/* Espace Direct Live */}
          {activeSection === 'live_management' && <LiveManagementSection />}


          {/* News Ticker */}

          {/* Results Management */}
          {activeSection === 'results' && <ResultsManagementSection />}

          {/* Discipline & Cards */}
          {activeSection === 'discipline' && <DisciplineSection />}

          {/* Point Deductions */}
          {activeSection === 'penalties' && <PointsDeductionSection teams={teams} matches={matches} />}

          {/* Players */}
          {activeSection === 'players' && <PlayersSection />}

          {/* Club News */}
          {activeSection === 'club-news' && <ClubNewsSection />}

          {/* Ligue News */}
          {activeSection === 'news' && <NewsSection />}

          {/* Committees */}
          {activeSection === 'committees' && <CommitteesSection />}

          {/* Designations */}
          {activeSection === 'designations' && <DesignationsSection />}

          {/* Communiques */}
          {activeSection === 'communiques' && <CommuniquesSection />}

          {/* Media */}
          {activeSection === 'media' && <MediaSection />}

          {/* Settings */}
          {activeSection === 'settings' && <SettingsSection />}

          {/* Coming Soon Sections */}
          {['discipline'].includes(activeSection) && (
            <div style={styles.comingSoon}>
              <h1 style={styles.pageTitle}>Section en développement</h1>
              <p>Cette section sera bientôt disponible</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const styles = {
  dashboardWrap: { minHeight: '100vh', width: '100%', boxSizing: 'border-box', direction: 'ltr' },
  container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', background: '#f5f5f5', boxSizing: 'border-box', position: 'relative' },
  infoBoxSync: { background: '#fff3cd', border: '1px solid #ffeeba', color: '#856404', padding: '12px 15px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' },
  adminMenuYellowStrip: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    background: 'linear-gradient(180deg, rgba(255,193,7,0.18) 0%, rgba(255,152,0,0.12) 100%)',
    borderBottom: '3px solid rgba(255,193,7,0.6)',
    boxShadow: '0 4px 12px rgba(255,193,7,0.15)'
  },
  topBar: {
    height: 56,
    background: 'linear-gradient(90deg, #1a1a1a 0%, #2d2d2d 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '16px',
    paddingRight: '16px',
    gap: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
  },
  topBarBrand: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  topBarLogoCarreau: {
    width: 36,
    height: 36,
    minWidth: 36,
    minHeight: 36,
    padding: 0,
    border: 'none',
    borderRadius: 8,
    background: 'rgba(255,255,255,0.12)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0
  },
  topBarLogoImg: { width: '100%', height: '100%', objectFit: 'contain' },
  topBarTitle: { margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#c41e3a' },
  topBarTitleSmall: { margin: 0, fontSize: '15px', fontWeight: 'bold', color: '#c41e3a' },
  topBarVersion: { fontSize: '11px', color: 'rgba(255,255,255,0.7)' },
  topBarNav: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px',
    flex: 1,
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '8px 0',
    WebkitOverflowScrolling: 'touch'
  },
  menuItemHorizontal: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    transition: 'background 0.2s',
    flexShrink: 0
  },
  menuItemHorizontalActive: { background: '#c41e3a', color: 'white' },
  menuIconHorizontal: { fontSize: '16px', flexShrink: 0 },
  sidebar: {
    background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)',
    color: 'white',
    position: 'fixed',
    left: 0,
    top: 0,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 100,
    transition: 'width 0.25s ease'
  },
  sidebarHeader: {
    padding: '16px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    position: 'relative',
    minHeight: '52px',
    flexShrink: 0
  },
  sidebarTitle: { margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#c41e3a' },
  version: { fontSize: '11px', color: '#999', margin: 0 },
  collapseBtn: { position: 'absolute', top: '12px', right: '12px', background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sidebarNav: {
    padding: '12px 0',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    paddingLeft: '16px',
    paddingRight: '16px'
  },
  menuItem: {
    width: '100%',
    minWidth: 0,
    padding: '15px 16px',
    background: 'transparent',
    border: 'none',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '15px',
    textAlign: 'left',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
  },
  menuItemActive: { background: '#c41e3a', borderLeft: '4px solid white' },
  menuIcon: { fontSize: '18px', flexShrink: 0 },
  mainContent: {
    flex: 1,
    marginLeft: 0,
    padding: '24px',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflowX: 'auto',
    overflowY: 'auto',
    minHeight: '100vh',
    transition: 'margin-left 0.25s ease'
  },
  pageTitle: { fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' },
  pageSubtitle: { fontSize: '16px', color: '#666', marginBottom: '30px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' },
  statCard: { background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', textAlign: 'center' },
  statIcon: { fontSize: '40px', color: '#c41e3a', marginBottom: '15px' },
  statNumber: { fontSize: '36px', fontWeight: 'bold', margin: '10px 0' },
  statLabel: { fontSize: '16px', color: '#666' },
  groupsStatsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '15px' },
  groupStatCard: { background: 'linear-gradient(135deg, #c41e3a18, #c41e3a08)', padding: '20px', borderRadius: '12px', textAlign: 'center', border: '1px solid rgba(196,30,58,0.2)' },
  groupStatLabel: { display: 'block', fontSize: '14px', color: '#666', marginBottom: '8px' },
  groupStatNumber: { display: 'block', fontSize: '28px', fontWeight: 'bold', color: '#c41e3a' },
  groupStatTeams: { fontSize: '12px', color: '#999' },
  quickLinksGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' },
  quickLinkBtn: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', background: '#f5f5f5', border: '2px solid #e0e0e0', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', transition: 'all 0.2s' },
  quickLinkIcon: { fontSize: '20px', color: '#c41e3a' },
  card: { background: 'white', padding: '30px', borderRadius: '15px', marginBottom: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', maxWidth: '100%', boxSizing: 'border-box' },
  cardTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' },
  form: { display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '100%', boxSizing: 'border-box' },
  input: { padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box', maxWidth: '100%' },
  scoreRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
  formActions: { display: 'flex', gap: '10px', flexWrap: 'wrap', maxWidth: '100%' },
  submitBtn: { padding: '15px', background: '#c41e3a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', flex: '1 1 auto', minWidth: '120px' },
  cancelBtn: { padding: '15px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', flexShrink: 0 },
  teamsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' },
  teamCard: { background: '#f9f9f9', padding: '20px', borderRadius: '10px', textAlign: 'center', border: '2px solid #e0e0e0' },
  teamLogo: { width: '80px', height: '80px', objectFit: 'contain', marginBottom: '15px' },
  teamName: { fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' },
  teamInfo: { fontSize: '14px', color: '#666', marginBottom: '5px' },
  cardActions: { display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'center' },
  editBtn: { padding: '8px 15px', background: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' },
  deleteBtn: { padding: '8px 15px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' },
  matchFilters: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '16px' },
  filterInput: { padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', minWidth: '140px' },
  filterSelect: { padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', minWidth: '160px' },
  filterReset: { padding: '10px 14px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  matchCount: { fontSize: '14px', color: '#666', marginBottom: '16px' },
  matchCardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  matchCard: { background: '#f9f9f9', border: '2px solid #e0e0e0', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  matchCardRound: { fontSize: '12px', color: '#666', fontWeight: '600' },
  matchCardDate: { fontSize: '13px', color: '#555' },
  matchCardTeams: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 },
  matchCardTeam: { fontSize: '14px', fontWeight: '600', lineHeight: 1.3 },
  matchCardScore: { fontSize: '18px', fontWeight: 'bold', color: '#c41e3a', textAlign: 'center', margin: '4px 0' },
  matchCardActions: { display: 'flex', gap: '8px', marginTop: '8px' },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginTop: '24px', flexWrap: 'wrap' },
  pageBtn: { padding: '10px 20px', background: '#1a365d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  pageInfo: { fontSize: '14px', color: '#666' },
  matchRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: '#f9f9f9', borderRadius: '8px', marginBottom: '10px' },
  matchInfo: { display: 'flex', alignItems: 'center', gap: '15px', flex: 1 },
  matchActions: { display: 'flex', gap: '10px' },
  matchTeam: { fontSize: '15px', fontWeight: '600', minWidth: '120px' },
  matchScore: { fontSize: '18px', fontWeight: 'bold', color: '#c41e3a', minWidth: '60px', textAlign: 'center' },
  matchDate: { fontSize: '13px', color: '#666' },
  comingSoon: { background: 'white', padding: '60px', borderRadius: '15px', textAlign: 'center' },
  addTeamBtn: { marginBottom: '20px', padding: '12px 24px', background: '#c41e3a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px', boxSizing: 'border-box' },
  modalBox: { background: 'white', borderRadius: '16px', maxWidth: '560px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '2px solid #e0e0e0' },
  modalTitle: { margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#333' },
  modalClose: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: '#666', borderRadius: '8px' }
};

export default AdminDashboardV2;
